import { Paper, Box, Typography, useTheme } from '@mui/material';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import type { UsageLogsStats } from '@/types/domain';
import { getActionChartColor, getActionLabel } from '../utils';

interface StatsChartsProps {
  dailyActivations: UsageLogsStats['dailyActivations'];
  actionDistribution: UsageLogsStats['actionDistribution'];
  activeDevices: UsageLogsStats['activeDevices'];
  licenseKeyStats: UsageLogsStats['licenseKeyStats'];
}

// Recharts generic typings can be strict in TS projects. These aliases keep JSX readable.
const SafeLineChart = LineChart as any;
const SafeLine = Line as any;
const SafePieChart = PieChart as any;
const SafePie = Pie as any;
const SafeCell = Cell as any;
const SafeBarChart = BarChart as any;
const SafeBar = Bar as any;
const SafeXAxis = XAxis as any;
const SafeYAxis = YAxis as any;
const SafeCartesianGrid = CartesianGrid as any;
const SafeTooltip = Tooltip as any;
const SafeLegend = Legend as any;
const SafeResponsiveContainer = ResponsiveContainer as any;

export default function StatsCharts({
  dailyActivations,
  actionDistribution,
  activeDevices,
  licenseKeyStats,
}: StatsChartsProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const actionChartData = actionDistribution.map((item) => ({
    name: getActionLabel(item.action, t),
    value: item.count,
    action: item.action,
  }));

  const deviceChartData = activeDevices.map((item) => ({
    name: item.deviceName || (item.hardwareId ? item.hardwareId.substring(0, 8) : `Device #${item.deviceId}`),
    count: item.count,
    hardwareId: item.hardwareId || '',
  }));

  const licenseKeyChartData = licenseKeyStats.map((item) => ({
    name: item.licenseKey.length > 15 ? `${item.licenseKey.substring(0, 12)}...` : item.licenseKey,
    fullName: item.licenseKey,
    logCount: item.logCount,
    usedSlots: item.activationCount,
    remainingSlots: item.remainingSlots,
  }));

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
      <Paper sx={{ p: 2, flex: '1 1 100%', minWidth: 300 }}>
        <Typography variant="h6" gutterBottom>
          {t('logs.dailyTrend')}
        </Typography>
        <Box height={250}>
          <SafeResponsiveContainer width="100%" height="100%">
            <SafeLineChart data={dailyActivations}>
              <SafeCartesianGrid strokeDasharray="3 3" />
              <SafeXAxis dataKey="date" tick={{ fontSize: 12 }} stroke={theme.palette.text.secondary} />
              <SafeYAxis stroke={theme.palette.text.secondary} />
              <SafeTooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.shape.borderRadius,
                }}
                labelFormatter={(label: string) => `${t('logs.date')}: ${label}`}
              />
              <SafeLegend />
              <SafeLine
                type="monotone"
                dataKey="count"
                name={t('logs.activations')}
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={{ fill: theme.palette.primary.main, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </SafeLineChart>
          </SafeResponsiveContainer>
        </Box>
      </Paper>

      {actionChartData.length > 0 && (
        <Paper sx={{ p: 2, flex: '1 1 45%', minWidth: 300 }}>
          <Typography variant="h6" gutterBottom>
            {t('logs.actionDistribution')}
          </Typography>
          <Box height={250}>
            <SafeResponsiveContainer width="100%" height="100%">
              <SafePieChart>
                <SafePie
                  data={actionChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: { name: string; percent: number }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  dataKey="value"
                >
                  {actionChartData.map((entry: { action: string }, index: number) => (
                    <SafeCell key={`cell-${index}`} fill={getActionChartColor(entry.action)} />
                  ))}
                </SafePie>
                <SafeTooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: theme.shape.borderRadius,
                  }}
                />
              </SafePieChart>
            </SafeResponsiveContainer>
          </Box>
        </Paper>
      )}

      {deviceChartData.length > 0 && (
        <Paper sx={{ p: 2, flex: '1 1 45%', minWidth: 300 }}>
          <Typography variant="h6" gutterBottom>
            {t('logs.activeDevices')}
          </Typography>
          <Box height={250}>
            <SafeResponsiveContainer width="100%" height="100%">
              <SafeBarChart data={deviceChartData} layout="vertical">
                <SafeCartesianGrid strokeDasharray="3 3" />
                <SafeXAxis type="number" stroke={theme.palette.text.secondary} />
                <SafeYAxis
                  type="category"
                  dataKey="name"
                  width={100}
                  tick={{ fontSize: 11 }}
                  stroke={theme.palette.text.secondary}
                />
                <SafeTooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: theme.shape.borderRadius,
                  }}
                  formatter={(value: number) => [value, t('logs.logCount')]}
                />
                <SafeBar dataKey="count" fill={theme.palette.secondary.main} radius={[0, 4, 4, 0]} />
              </SafeBarChart>
            </SafeResponsiveContainer>
          </Box>
        </Paper>
      )}

      {licenseKeyChartData.length > 0 && (
        <Paper sx={{ p: 2, flex: '1 1 100%', minWidth: 300 }}>
          <Typography variant="h6" gutterBottom>
            {t('logs.logsByLicenseKey')}
          </Typography>
          <Box height={250}>
            <SafeResponsiveContainer width="100%" height="100%">
              <SafeBarChart data={licenseKeyChartData}>
                <SafeCartesianGrid strokeDasharray="3 3" />
                <SafeXAxis dataKey="name" tick={{ fontSize: 11 }} stroke={theme.palette.text.secondary} />
                <SafeYAxis stroke={theme.palette.text.secondary} />
                <SafeTooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: theme.shape.borderRadius,
                  }}
                  formatter={(value: number, name: string) => {
                    if (name === 'logCount') return [value, t('logs.logCount')];
                    if (name === 'usedSlots') return [value, t('logs.usedSlots')];
                    if (name === 'remainingSlots') return [value, t('logs.remainingSlots')];
                    return [value, name];
                  }}
                  labelFormatter={(label: string, payload: any) => {
                    if (payload?.[0]) {
                      return payload[0].payload.fullName;
                    }
                    return label;
                  }}
                />
                <SafeLegend />
                <SafeBar
                  dataKey="logCount"
                  name={t('logs.logCount')}
                  fill={theme.palette.primary.main}
                  radius={[4, 4, 0, 0]}
                />
              </SafeBarChart>
            </SafeResponsiveContainer>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
